/* CONSTANTE REAL — écran de validation humaine : une ambiguïté à la fois,
   4 actions, temps mesuré (Human Configuration Burden). Chaque décision est
   appliquée immédiatement au canonique et tracée HUMAN_VALIDATED. */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { CheckCircle2, Loader2, ShieldQuestion, Timer } from "lucide-react";

const KIND_LABEL = { duplicate: "Doublon", relationship: "Relation proposée", name_resolution: "Résolution de nom" };

export default function RealValidation() {
  const [queue, setQueue] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [selected, setSelected] = useState(null);
  const [closeResult, setCloseResult] = useState(null);
  const [sealResult, setSealResult] = useState(null);
  const shownAt = useRef(Date.now());

  const load = () => api.get("/workspace/real-validation/queue")
    .then((r) => { setQueue(r.data); shownAt.current = Date.now(); setNote(""); setSelected(null); })
    .catch((e) => notify.error(fmtError(e)));
  useEffect(() => { load(); }, []);

  const item = queue?.pending?.[0];

  const decide = async (action) => {
    setBusy(true);
    try {
      const r = await api.post(`/workspace/real-validation/${item.id}/decide`, {
        action, note, selected, elapsed_ms: Date.now() - shownAt.current,
      });
      if (r.data.applied) notify.success(r.data.applied);
      load();
    } catch (e) { notify.error(fmtError(e)); }
    setBusy(false);
  };

  const closeAndSeal = async () => {
    setBusy(true);
    try {
      const r = await api.post("/workspace/real-validation/close");
      setCloseResult(r.data);
      const s = await api.post("/workspace/seals/t0");
      setSealResult(s.data);
      notify.success("T0 prospectif scellé");
    } catch (e) { notify.error(fmtError(e)); }
    setBusy(false);
  };

  if (!queue) return <div className="p-6 text-zinc-500 text-sm"><Loader2 className="w-4 h-4 animate-spin inline" /> Chargement…</div>;

  const mins = Math.round(queue.total_human_ms / 600) / 100;

  return (
    <div className="p-6 max-w-3xl space-y-4" data-testid="real-validation-page">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-white flex items-center gap-2">
          <ShieldQuestion className="w-5 h-5 text-[#EF9F27]" /> Validation — Constante Real
        </h1>
        <div className="text-[11px] text-zinc-400 flex items-center gap-2" data-testid="hcb-timer">
          <Timer className="w-3.5 h-3.5" /> {queue.decided} décidés · {queue.pending.length} restants · {mins} min humaines
        </div>
      </div>

      {sealResult ? (
        <div className="rounded border border-[#1D9E75]/40 bg-[#1D9E75]/10 p-4 space-y-2" data-testid="seal-result">
          <div className="text-[#1D9E75] font-semibold flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> T0 prospectif scellé</div>
          <div className="text-xs text-zinc-300 font-mono break-all">hash : {sealResult.hash}</div>
          <div className="text-xs text-zinc-400">{JSON.stringify(sealResult.counts)}</div>
          {closeResult && <div className="text-xs text-zinc-400">Périmètre final : {JSON.stringify(closeResult.perimeter)} · relations : {closeResult.relationships} · doublons canoniques restants : {closeResult.canonical_duplicates_remaining} · temps humain total : {Math.round(closeResult.total_human_ms / 60000)} min</div>}
          <div className="text-xs text-[#EF9F27]">Tout ce qui arrive après ce scellement est matière prospective. Prochaine étape : Accounting Wave B.</div>
        </div>
      ) : !item ? (
        <div className="rounded border border-white/10 bg-white/[0.02] p-6 text-center space-y-3" data-testid="queue-empty">
          <div className="text-sm text-zinc-300">File vide — toutes les ambiguïtés sont arbitrées.</div>
          <button onClick={closeAndSeal} disabled={busy} data-testid="close-and-seal-btn"
            className="px-4 py-2 rounded bg-[#1D9E75]/20 border border-[#1D9E75]/40 text-[#1D9E75] hover:bg-[#1D9E75]/30 text-sm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Clôturer la validation → sceller le T0 prospectif"}
          </button>
        </div>
      ) : (
        <div className="rounded border border-white/10 bg-white/[0.02] p-4 space-y-3" data-testid="validation-card">
          <div className="flex items-center gap-2">
            <span className="text-[10px] px-2 py-0.5 rounded-full border border-[#7F77DD]/40 text-[#7F77DD]">{KIND_LABEL[item.kind] || item.kind}</span>
            <span className="text-[10px] text-zinc-500">confiance : {item.confidence}</span>
          </div>
          <div className="text-base text-white font-medium" data-testid="validation-title">{item.title}</div>
          <div className="text-xs space-y-1">
            <div className="text-zinc-400">Actuel : <span className="text-zinc-200">{item.current}</span></div>
            <div className="text-zinc-400">Proposition NOAM : <span className="text-zinc-200">{item.proposed}</span></div>
            <div className="text-zinc-400">Preuve : <span className="text-zinc-300">{item.evidence}</span></div>
            <div className="text-[#EF9F27]">Pourquoi incertain : {item.uncertainty}</div>
          </div>
          {item.candidates?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {item.candidates.map((c) => (
                <button key={c} onClick={() => setSelected(c)} data-testid={`candidate-${c}`}
                  className={`px-2 py-1 rounded text-xs border ${selected === c
                    ? "border-[#1D9E75] text-[#1D9E75] bg-[#1D9E75]/10" : "border-white/15 text-zinc-300 hover:bg-white/5"}`}>
                  {c}
                </button>
              ))}
            </div>
          )}
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note / correction (optionnel — requis pour MODIFY)"
            data-testid="validation-note" className="w-full bg-black/30 border border-white/10 rounded px-2 py-1.5 text-xs text-white" />
          <div className="flex flex-wrap gap-2">
            <button onClick={() => decide("VALIDATE")} disabled={busy} data-testid="btn-validate"
              className="px-3 py-1.5 rounded bg-[#1D9E75]/20 border border-[#1D9E75]/40 text-[#1D9E75] text-xs">VALIDATE</button>
            <button onClick={() => decide("MODIFY")} disabled={busy || !note.trim()} data-testid="btn-modify"
              className="px-3 py-1.5 rounded bg-[#378ADD]/20 border border-[#378ADD]/40 text-[#378ADD] text-xs disabled:opacity-40">MODIFY</button>
            <button onClick={() => decide("REJECT")} disabled={busy} data-testid="btn-reject"
              className="px-3 py-1.5 rounded bg-[#E24B4A]/20 border border-[#E24B4A]/40 text-[#E24B4A] text-xs">REJECT</button>
            <button onClick={() => decide("DEFER")} disabled={busy} data-testid="btn-defer"
              className="px-3 py-1.5 rounded border border-white/15 text-zinc-400 text-xs">DEFER</button>
          </div>
        </div>
      )}
    </div>
  );
}
